import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';

export interface SelectOption<V extends string | number = number> {
  value: V;
  label: string;
  /** Options sharing a group are listed under that heading, in the order given */
  group?: string;
}

interface SearchableSelectProps<V extends string | number> {
  options: SelectOption<V>[];
  value: V | null | undefined;
  onChange: (value: V | null) => void;
  /** Shown when nothing is selected */
  placeholder?: string;
  /** Adds a first option that clears the selection, e.g. "All Organizations" */
  clearLabel?: string;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

/** Every search word must appear in the label or its group */
export const matchesSearch = (option: { label: string; group?: string }, search: string): boolean => {
  const haystack = `${option.label} ${option.group ?? ''}`.toLowerCase();
  return search.toLowerCase().split(/\s+/).filter(Boolean).every(word => haystack.includes(word));
};

/**
 * A select you can type into to filter, for long lists like roles and
 * organizations. Arrow keys move, Enter picks, Escape closes.
 */
export const SearchableSelect = <V extends string | number>({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  clearLabel,
  id,
  ariaLabel,
  disabled = false,
  className = '',
}: SearchableSelectProps<V>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const selected = options.find(o => o.value === value) ?? null;

  // Selectable rows in display order; the clear row (value null) comes first
  const rows = useMemo(() => {
    const filtered = options.filter(o => matchesSearch(o, search));
    const clearRow = clearLabel && !search ? [{ value: null, label: clearLabel } as const] : [];
    return [...clearRow, ...filtered] as Array<{ value: V | null; label: string; group?: string }>;
  }, [options, search, clearLabel]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keep the highlighted row visible while arrowing through a long list
  useEffect(() => {
    if (!isOpen) return;
    listRef.current?.querySelector<HTMLElement>(`[data-row="${highlight}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [highlight, isOpen]);

  const open = () => {
    if (disabled) return;
    setIsOpen(true);
    const selectedRow = rows.findIndex(r => r.value === (value ?? null));
    setHighlight(selectedRow >= 0 ? selectedRow : 0);
  };

  const close = () => {
    setIsOpen(false);
    setSearch('');
  };

  const choose = (row: { value: V | null }) => {
    onChange(row.value);
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        open();
        return;
      }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight(h => Math.min(Math.max(h + step, 0), Math.max(rows.length - 1, 0)));
    } else if (e.key === 'Enter') {
      if (isOpen && rows[highlight]) {
        e.preventDefault();
        choose(rows[highlight]);
      }
    } else if (e.key === 'Escape') {
      if (isOpen) {
        e.preventDefault();
        close();
      }
    } else if (e.key === 'Tab') {
      close();
    }
  };

  const shownText = isOpen ? search : (selected?.label ?? '');
  const shownPlaceholder = isOpen ? (selected?.label ?? 'Type to search...') : (clearLabel ?? placeholder);

  let lastGroup: string | undefined;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={isOpen && rows[highlight] ? `${listId}-${highlight}` : undefined}
        autoComplete="off"
        value={shownText}
        placeholder={shownPlaceholder}
        disabled={disabled}
        onFocus={open}
        onClick={() => { if (!isOpen) open(); }}
        onChange={(e) => {
          setSearch(e.target.value);
          setHighlight(0);
          if (!isOpen) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-8 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onMouseDown={(e) => {
          e.preventDefault();
          if (isOpen) {
            close();
          } else {
            inputRef.current?.focus();
            open();
          }
        }}
        className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400"
        disabled={disabled}
      >
        <HiChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full min-w-[12rem] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-700"
        >
          {rows.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matches</li>
          )}
          {rows.map((row, index) => {
            const heading = row.group && row.group !== lastGroup ? row.group : null;
            lastGroup = row.group;
            const isSelected = row.value === (value ?? null);
            return (
              <li key={`${String(row.value)}-${index}`} role="presentation">
                {heading && (
                  <div className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {heading}
                  </div>
                )}
                <div
                  id={`${listId}-${index}`}
                  data-row={index}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(row);
                  }}
                  onMouseEnter={() => setHighlight(index)}
                  className={`cursor-pointer px-3 py-2 text-sm ${
                    index === highlight ? 'bg-blue-50 dark:bg-gray-600' : ''
                  } ${isSelected ? 'font-semibold text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'}`}
                >
                  {row.label}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SearchableSelect;
